import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "react-router-dom";
import { LoginRequestSchema, type LoginRequest } from "@pkhoffice/shared";
import { login } from "@/features/auth/api";
import { useAuthStore } from "@/features/auth/store";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { Building2, Lock, User as UserIcon } from "lucide-react";

export function LoginPage() {
  const nav = useNavigate();
  const setTokens = useAuthStore((s) => s.setTokens);
  const [err, setErr] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<LoginRequest>({
    resolver: zodResolver(LoginRequestSchema),
  });

  async function onSubmit(v: LoginRequest) {
    setErr(null);
    try {
      const res = await login(v);
      setTokens(res.accessToken, res.refreshToken, res.user);
      nav("/dashboard", { replace: true });
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { msg?: string } } }).response?.data?.msg;
      setErr(msg ?? "Đăng nhập thất bại");
    }
  }

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-[1.05fr_1fr] bg-canvas">
      {/* Left — brand story */}
      <div
        className="relative hidden lg:flex flex-col justify-between p-12 text-[hsl(var(--sidebar-foreground))] overflow-hidden"
        style={{
          background:
            "linear-gradient(160deg, hsl(222 47% 13%) 0%, hsl(215 100% 20%) 55%, hsl(200 95% 28%) 100%)",
        }}
      >
        <div className="absolute inset-0 bg-grain-overlay opacity-60 pointer-events-none" />
        <div className="absolute -left-24 -top-24 w-96 h-96 rounded-full bg-white/5 blur-3xl" />
        <div className="absolute -right-16 bottom-20 w-72 h-72 rounded-full bg-[hsl(200_95%_45%)]/20 blur-3xl" />

        <div className="relative flex items-center gap-3">
          <div className="h-11 w-11 rounded-md brand-chip grid place-items-center font-bold text-base shadow-lifted">
            PK
          </div>
          <div className="font-serif text-2xl italic leading-none">
            PKH<span className="not-italic font-sans font-semibold">Office</span>
          </div>
        </div>

        <div className="relative max-w-lg">
          <div className="font-serif italic text-5xl leading-[1.05] text-white mb-4">
            Văn phòng số,
            <br />
            <span className="text-[hsl(200_95%_75%)]">vận hành mượt mà.</span>
          </div>
          <p className="text-white/70 text-base leading-relaxed max-w-md">
            Hệ thống quản lý văn bản, nhiệm vụ và lịch họp cho đơn vị — tiếp nối PiOffice cũ với
            giao diện hiện đại hơn.
          </p>
        </div>

        <div className="relative flex items-center gap-4 text-white/60 text-xs uppercase tracking-widest">
          <Building2 className="h-4 w-4" strokeWidth={1.5} />
          Phát triển bởi Phòng Kỹ thuật cơ năng
        </div>
      </div>

      {/* Right — form */}
      <div className="relative flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          <div className="mb-8 lg:hidden flex items-center gap-3">
            <div className="h-10 w-10 rounded-md brand-chip grid place-items-center font-bold shadow-inner">
              PK
            </div>
            <div className="font-serif text-2xl italic">
              PKH<span className="not-italic font-sans font-semibold">Office</span>
            </div>
          </div>

          <div className="mb-6">
            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
              Đăng nhập
            </div>
            <h1 className="font-serif italic text-4xl leading-tight text-foreground">
              Chào mừng trở lại.
            </h1>
          </div>

          <form
            onSubmit={handleSubmit(onSubmit)}
            className="rounded-lg bg-background border border-border shadow-lifted p-6 space-y-4"
          >
            <div>
              <label
                htmlFor="loginName"
                className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5"
              >
                Tên đăng nhập
              </label>
              <div className="relative">
                <UserIcon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  id="loginName"
                  className="w-full h-10 pl-10 pr-3 border border-input rounded-sm bg-background text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  {...register("loginName")}
                  autoComplete="username"
                  placeholder="Mã nhân viên"
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="password"
                className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5"
              >
                Mật khẩu
              </label>
              <div className="relative">
                <Lock className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  id="password"
                  type="password"
                  className="w-full h-10 pl-10 pr-3 border border-input rounded-sm bg-background text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  {...register("password")}
                  autoComplete="current-password"
                  placeholder="••••••••"
                />
              </div>
            </div>

            {err && (
              <div className="px-3 py-2 rounded-sm bg-destructive/10 border border-destructive/20 text-destructive text-sm">
                {err}
              </div>
            )}

            <Button type="submit" className="w-full" disabled={isSubmitting} size="lg">
              {isSubmitting ? "Đang đăng nhập…" : "Đăng nhập"}
            </Button>

            <p className="text-xxs text-center text-muted-foreground pt-2">
              Dùng chung tài khoản với hệ PiOffice · vấn đề đăng nhập, liên hệ IT Support
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
